/**
 * MSW handlers for the contractor-payment demo.
 *
 * The fixtures here are **wire-shaped** `ContractorPayment` rows, not view models, and they are
 * paginated the way the server paginates — so the demo exercises the real query hook, the real
 * accumulation loop and the real adapter rather than assuming they work. Modelled on
 * `Team/demo/demoHandlers.ts`.
 *
 * This is where the flow can be reviewed end to end without a payroll-connected member.
 */

// msw is a devDependency but this file is only used by Storybook, which includes devDependencies in its build
// eslint-disable-next-line import/no-extraneous-dependencies
import { delay, http, HttpResponse } from 'msw';
import dayjs from 'dayjs';

import { ContractorPayment, ContractorPaymentWageTypeEnum } from 'services/payroll/api/generated/v1/model';

const PAYMENTS_URL = '*/api/payroll/v1/contractor-payments/';

/**
 * Fixture dates are relative, not absolute. A hardcoded date drops out of the page's default
 * three-month window as soon as enough real time passes, and the demo would quietly go empty —
 * which now that the handler actually honours the window below, it really would.
 */
const daysAgo = (days: number): string => dayjs().subtract(days, 'day').format('YYYY-MM-DD');

const buildPayment = (overrides: Partial<ContractorPayment> = {}): ContractorPayment => ({
    amount: '1200.00',
    bonus: null,
    contractor_name: 'Tim Cowherd',
    contractor_payment_uuid: 'payment-1',
    contractor_uuid: 'contractor-1',
    group_uuid: 'group-1',
    hourly_rate: null,
    hours: null,
    invoice_number: '',
    may_cancel: false,
    memo: '',
    payment_date: daysAgo(30),
    payment_method: 'direct_deposit',
    reimbursement: null,
    status: 'paid',
    wage: '1200.00',
    wage_total: '1200.00',
    wage_type: ContractorPaymentWageTypeEnum.fixed,
    ...overrides,
});

/**
 * A deliberately awkward history: a grouped payment, a lone ungrouped one, a group still
 * processing, a group whose payments disagree, a payment whose amount failed to load, and a status
 * string the adapter has never seen. Every one of those collapses to a different row.
 */
const PAYMENTS: ContractorPayment[] = [
    buildPayment({ contractor_payment_uuid: 'payment-1', contractor_name: 'Tim Cowherd', amount: '1200.00' }),
    buildPayment({ contractor_payment_uuid: 'payment-2', contractor_name: 'Priya Natarajan', amount: '2450.00' }),
    buildPayment({ contractor_payment_uuid: 'payment-3', contractor_name: 'Sofia Reyes', amount: '1000.00' }),

    // No group: a single payment stands as its own row rather than vanishing into a null bucket.
    buildPayment({
        contractor_payment_uuid: 'payment-4',
        contractor_name: 'Tim Cowherd',
        group_uuid: null,
        amount: '600.00',
        payment_date: daysAgo(44),
        hourly_rate: '60.0000',
        hours: '10',
        wage_type: ContractorPaymentWageTypeEnum.hourly,
    }),

    // Still moving — no receipt to offer yet, and the one row the provider still lets a member
    // call back.
    buildPayment({
        contractor_payment_uuid: 'payment-5',
        contractor_name: 'Priya Natarajan',
        group_uuid: 'group-3',
        status: 'pending',
        amount: '900.00',
        payment_date: daysAgo(16),
        may_cancel: true,
    }),

    // A split group: one paid, one cancelled. No single status is true of it.
    buildPayment({
        contractor_payment_uuid: 'payment-6',
        contractor_name: 'Sofia Reyes',
        group_uuid: 'group-4',
        status: 'paid',
        amount: '400.00',
        payment_date: daysAgo(60),
    }),
    buildPayment({
        contractor_payment_uuid: 'payment-7',
        contractor_name: 'Tim Cowherd',
        group_uuid: 'group-4',
        status: 'cancelled',
        // Null, not zero: this figure failed to load, so the group's total is unavailable rather
        // than a partial sum that understates what moved.
        amount: null,
        payment_date: daysAgo(60),
    }),

    // A status nothing here recognises. Neutral tag, never "Paid".
    buildPayment({
        contractor_payment_uuid: 'payment-8',
        contractor_name: 'Priya Natarajan',
        group_uuid: 'group-5',
        status: 'reversed_by_provider',
        amount: '0.00',
        payment_date: daysAgo(73),
    }),
];

/**
 * The window the query sends — inclusive on `payment_date`, either bound optional. A payment with
 * no date has no position in a window, so a bounded request leaves it out; an unbounded one keeps
 * everything. ISO dates compare correctly as strings, so no parsing is needed.
 */
const withinWindow = (payment: ContractorPayment, startDate: string | null, endDate: string | null): boolean => {
    if (!startDate && !endDate) {
        return true;
    }

    if (!payment.payment_date) {
        return false;
    }

    return (!startDate || payment.payment_date >= startDate) && (!endDate || payment.payment_date <= endDate);
};

/**
 * Filters by the window first, then paginates the way the server does — so the accumulation loop is
 * exercised for real and the page's date picker actually changes the result. Ignoring the window
 * would hand back the same rows and the same count for every range, which makes the one control on
 * the page look broken in the only place it can be reviewed.
 */
const paymentsHandler = http.get(PAYMENTS_URL, ({ request }) => {
    const url = new URL(request.url);
    const inWindow = PAYMENTS.filter((payment) =>
        withinWindow(payment, url.searchParams.get('start_date'), url.searchParams.get('end_date'))
    );

    const limit = Number(url.searchParams.get('limit') ?? inWindow.length);
    const offset = Number(url.searchParams.get('offset') ?? 0);

    return HttpResponse.json({
        as_of: dayjs().toISOString(),
        count: inWindow.length,
        results: inWindow.slice(offset, offset + limit),
    });
});

/**
 * Never answers. The page's loading state is otherwise a flash between two renders, so there is no
 * way to review it; holding the request open makes it a state you can sit and look at.
 */
export const loadingPaymentsHandler = http.get(PAYMENTS_URL, async () => {
    await delay('infinite');

    return HttpResponse.json({ as_of: null, count: 0, results: [] });
});

/**
 * Claims far more payments than the hook's page bound will ever fetch, so the accumulation loop
 * gives up and `isTruncated` comes back true. Each page answers with two rows under fresh uuids —
 * the bound is what is being demonstrated, not the volume, and re-using one uuid would collapse
 * every page into a single group.
 */
export const truncatedPaymentsHandler = http.get(PAYMENTS_URL, ({ request }) => {
    const url = new URL(request.url);
    const offset = Number(url.searchParams.get('offset') ?? 0);

    return HttpResponse.json({
        as_of: dayjs().toISOString(),
        count: 100_000,
        results: PAYMENTS.slice(0, 2).map((payment, index) => ({
            ...payment,
            contractor_payment_uuid: `truncated-payment-${offset + index}`,
            group_uuid: `truncated-group-${offset + index}`,
        })),
    });
});

/** The read failing outright — what every member saw while the endpoint was a 501 stub. */
export const notImplementedPaymentsHandler = http.get(PAYMENTS_URL, () =>
    HttpResponse.json({ error: { code: 'NOT_IMPLEMENTED' } }, { status: 501 })
);

/** A member with no contractor payments in the window. */
export const emptyPaymentsHandler = http.get(PAYMENTS_URL, () =>
    HttpResponse.json({ as_of: null, count: 0, results: [] })
);

const allDemoHandlers = { contractorPayments: paymentsHandler };

export default allDemoHandlers;
